import type {
  HostFloorPlanTable,
  TableTimer,
} from 'restaurantAdmin/floorPlans/apiHelpers';
import { useReservationServiceContext } from 'restaurantAdmin/reservations/service/state/ReservationServiceContext';
import { useDroppable } from '@dnd-kit/core';
import { kebabCase } from 'lodash-es';
import { SeatedFloorPlanTable } from './SeatedFloorPlanTable';
import { UnoccupiedFloorPlanTable } from './UnoccupiedFloorPlanTable';

interface SeatedFloorPlanTableFactoryProps {
  timer?: TableTimer | null;
  calculateIsHighlighted?: (floorPlanTable: HostFloorPlanTable) => boolean;
  floorPlanTable: HostFloorPlanTable;
  handleTableOnClick: (
    floorPlanTable: HostFloorPlanTable,
  ) => void | Promise<void>;
  tableIconScale: number;
  enableSeatedTableClick?: boolean;
}
export const SeatedFloorPlanTableFactory = ({
  timer = null,
  calculateIsHighlighted = () => false,
  floorPlanTable,
  handleTableOnClick,
  tableIconScale,
  enableSeatedTableClick = false,
}: SeatedFloorPlanTableFactoryProps) => {
  const { seatedOccupant } = floorPlanTable;
  const { selectedOccupant } = useReservationServiceContext();
  const isSeated = seatedOccupant !== null;
  const { setNodeRef } = useDroppable({
    id: floorPlanTable.id,
    data: {
      accepts: !isSeated ? ['reservation', 'waitListEntry'] : [],
      floorPlanTable,
    },
  });
  const testId = kebabCase(`floor-plan-table-${floorPlanTable.name}`);

  if (isSeated) {
    return (
      <SeatedFloorPlanTable
        iconName={floorPlanTable.iconName}
        iconWidthScale={floorPlanTable.iconWidthScale}
        isAnyTableHighlighted={selectedOccupant != null}
        isDisabled={!enableSeatedTableClick}
        isHighlighted={calculateIsHighlighted(floorPlanTable)}
        key={floorPlanTable.id}
        left={floorPlanTable.left}
        name={floorPlanTable.name}
        orientation={floorPlanTable.orientation}
        ref={setNodeRef}
        seatedOccupant={seatedOccupant}
        tableIconScale={tableIconScale}
        testId={testId}
        top={floorPlanTable.top}
        assignedServer={floorPlanTable.assignedServer}
        handleTableOnClick={() => {
          if (enableSeatedTableClick) {
            void handleTableOnClick(floorPlanTable);
          }
        }}
      />
    );
  }

  return (
    <UnoccupiedFloorPlanTable
      timer={timer}
      handleTableOnClick={() => {
        void handleTableOnClick(floorPlanTable);
      }}
      iconName={floorPlanTable.iconName}
      iconWidthScale={floorPlanTable.iconWidthScale}
      isHighlighted={calculateIsHighlighted(floorPlanTable)}
      isSelected={false}
      key={floorPlanTable.id}
      left={floorPlanTable.left}
      name={floorPlanTable.name}
      orientation={floorPlanTable.orientation}
      ref={setNodeRef}
      tableIconScale={tableIconScale}
      testId={testId}
      top={floorPlanTable.top}
      assignedServer={floorPlanTable.assignedServer}
    />
  );
};
